// ── Monthly Portfolio Balance ─────────────────────────────────────────────
//
// Where the book's money went, by month and by category. Every year expands to
// its months; every column is one component of the balance walk.
//
// THE WALK, AND WHY IT TIES
//
//   Ending balance = running sum of
//       Initial Fundings + Draws - Received Prin + Participation Prin - ChargeOff
//
//   Component for component, that is how bsloanbalanceeod is computed. So the
//   last month of the walk MUST equal the sum of bsloanbalanceeod over the same
//   loans. The page checks that on every render and says so if it drifts — a
//   silent break here would be a reconciliation meeting, so it is made loud
//   instead.
//
//   IT TIES TO BS LOAN BALANCE, NOT LOAN BALANCE. BS is the settlement ledger:
//   what the borrower owes, less what has actually been settled with the
//   participants. The portfolio manager computes balance x (1 - pct) and then
//   charges the participant their share, so the two agree ONCE SETTLED and not
//   before. The ledger is what the balance sheet follows, so it is what the
//   walk reproduces. Loan Balance is shown on its own card, off the walk.
//
//   Participation principal is ADDED, not subtracted. The column is stored
//   signed and nets negative across the book, which is what puts the
//   balance-sheet balance at or below what the borrower owes. Subtracting it
//   would make the portfolio look bigger and still foot internally, which is
//   the dangerous kind of wrong.
//
// CUMULATIVE MEANS FROM INCEPTION, NOT FROM THE FIRST VISIBLE ROW
//   The API returns every month back to the first funding. Narrowing the date
//   range hides rows; it never restarts the walk. A balance that reset at the
//   top of the visible window would be a different number wearing the same
//   label.
//
// COMMITMENT IS DECLARED, NOT DERIVED
//   Terms.commitment is restated in full on every renewal, so summing it
//   counts a $30mm revolver again each year it rolls. Differencing consecutive
//   terms fixes that and introduces a worse problem: a loan transferred from
//   one facility to another shows an arithmetic increase that is not new
//   commitment to the book, and nothing in the data can tell the two apart.
//   Only the person booking the term knows.
//
//   So both commitment columns read Terms.newcommitmentamount, which they set
//   by hand. "New Commitments - New Loans" is the same field with
//   renewals excluded — every term_type except 'Loan Renewal'.
//
//   A declared field can be left blank, which would understate the report
//   silently. The endpoint returns `uncaptured` — commitment that rose against
//   the previous term while newcommitmentamount was zero — and the page
//   surfaces it rather than reporting the smaller number without comment.
//
// NOTHING IS NETTED
//   Every cash-flow column is gross, and the participants' leg of each is its
//   own column beside it: Received Fee sits next to Participation Fee rather
//   than being reduced by it. The Power BI report called the reduced figures
//   "Net", which meant a reader had to know that convention to know whether a
//   number already had participations in it. Four columns in a row, all
//   labelled Participation, cannot be misread the same way — and a reader who
//   wants the net figure can subtract two adjacent columns.
//
// FILTERS RE-WALK, THEY DO NOT RE-SLICE
//   Every filter narrows the loan set and the walk is recomputed over what is
//   left. Filter to one company and the balance line is that company's balance
//   from its first funding — not the portfolio's line with other loans erased.

import { useState, useEffect, useMemo, Fragment, useId } from "react";
import { useChartTheme, ChartThemeToggle, chartCardStyle, useWidth } from "../../../shared/chartTheme";
import { B, INK_MUTED, INK_PRIMARY, SURFACE,
         SURFACE_MUTED, HAIRLINE, HAIRLINE_FIRM } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, FilterSelect, Btn, SearchBar } from "../../../shared/ui";
import { fmt, fmtDate, fmtRatio, exportCSV, localFilter } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";

const num = v => parseFloat(v || 0) || 0;

// Compact money for axes and bar labels. The matrix shows full precision; a
// chart axis reading $142,915,536.68 is a wall, not a scale.
const short = n => {
  const a = Math.abs(n);
  const s = n < 0 ? "-" : "";
  if (a >= 1e9) return s + "$" + (a / 1e9).toFixed(a >= 1e10 ? 0 : 1) + "B";
  if (a >= 1e6) return s + "$" + (a / 1e6).toFixed(a >= 1e7 ? 0 : 1) + "M";
  if (a >= 1e3) return s + "$" + Math.round(a / 1e3) + "K";
  return s + "$" + Math.round(a);
};

// Money, but a true zero reads as a dash. A column of $0.00 where nothing
// happened is noise that hides the months where something did.
const money = n => (num(n) === 0 ? "—" : fmt(n));

// Negatives in parentheses, the way a ledger writes them. fmt() emits "$-1.00"
// for a negative, which reads badly in a column and is easy to skim past.
const signed = n => {
  const v = num(n);
  if (v === 0) return "—";
  return v < 0 ? "(" + fmt(-v).slice(1) + ")" : fmt(v);
};

// SIGN FLIP, FOR DISPLAY ONLY.
//
// Two groups of columns are stored one way round and read better the other:
//
//   Received Prin and Charge-Off are stored positive and REDUCE the balance,
//   so they show in parentheses.
//
//   The four participation columns are stored from the participant's side: a
//   positive figure is cash we paid OUT to them, a negative one is cash they
//   paid US. Flipped, the column reads the same direction as everything else
//   on the row — money coming in is positive.
//
// THIS NEVER TOUCHES THE ARITHMETIC. The balance walk reads the raw stored
// values, because that is what reproduces bsloanbalanceeod. Flipping the
// stored numbers instead of the rendered ones would break the tie-out while
// leaving the page looking entirely reasonable.
const cell = (c, v) => c.int ? (num(v) || "—")
                     : c.flip ? signed(-num(v))
                     : money(v);

// Participation amounts are stored from the participant's side: positive is
// cash we paid OUT to them. The card notes say the direction in words rather
// than relying on a sign the reader would have to decode, and they read the
// STORED value — the display flip belongs to the matrix, not to prose.
const partNote = v => {
  const n = num(v);
  if (!n) return "no participations";
  return n > 0 ? fmt(n) + " to participants" : fmt(-n) + " from participants";
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabel = ym => MONTHS[parseInt(ym.slice(5, 7), 10) - 1] || ym;
const yearOf = ym => ym.slice(0, 4);

// ── the columns ───────────────────────────────────────────────────────────
// `walk` marks the five that actually move the balance. They are tinted in the
// header so it is obvious at a glance which columns reconcile to the ending
// balance and which are there for context.
const COLS = [
  { key:"cnt",                label:"Cnt",              int:true,
    help:"Terms funded in the period. A loan that renews contributes a term each time, so this counts funding events, not loans." },
  { key:"newcommitment",      label:"New Commitments",
    help:"Sum of the New Commitment field on each funded term — set by the person booking the term, not derived. A renewal at the same size is zero; a transfer between facilities is whatever they judged it to be. Arithmetic cannot make that call, so it does not try." },
  { key:"newloancommitment",  label:"New Commitments - New Loans",
    help:"The same field with renewals excluded — every Term Type except 'Loan Renewal'. A renewal rolls commitment the book already had; a new loan, an amendment or a discretionary increase is commitment newly written. A term with no Term Type set counts here, since an untyped term is not a renewal." },
  { key:"restatedcommitment", label:"Restated Commitment", dim:true,
    help:"Raw SUM of the Commitment field across funded terms. Double-counts by design: a $30mm revolver appears again in full every year it renews. Here only as a cross-check against New Commitments." },
  { key:"initialfundings",    label:"Initial Fundings", walk:true,
    help:"Term funding amounts, by funding date. Increases the balance." },
  { key:"draws",              label:"Draws",            walk:true,
    help:"Revolver and DDTL draws, by funded date. Increases the balance." },
  { key:"receivedprin",       label:"Received Prin",    walk:true, flip:true,
    help:"Principal received from the borrower, non-reversed, by received date. Reduces the balance." },
  { key:"receivedint",        label:"Received Interest",
    help:"Interest received from the borrower. Revenue — it does not touch the balance." },
  { key:"receivedfee",        label:"Received Fee",
    help:"Fees received from the borrower, gross. The participants' share of them is its own column, not netted out of this one." },
  { key:"receivedother",      label:"Received Other",
    help:"Other receipts from the borrower, gross. Passthroughs are excluded — they are collected and paid straight back out, so they are never our money and do not bear on profitability." },
  { key:"partprin",           label:"Participation Prin", walk:true, flip:true,
    help:"Principal settled between us and the participants. Shown inverted: positive here means the participant funded us, in parentheses means we paid them. Tinted because, alone among the participation legs, it moves the balance — the walk uses the stored sign, not the displayed one." },
  { key:"partint",            label:"Participation Int", flip:true,
    help:"Interest settled with participants. Shown inverted: in parentheses means we paid it out to them." },
  { key:"partfee",            label:"Participation Fee", flip:true,
    help:"Fees settled with participants. Shown inverted: in parentheses means we paid it out to them." },
  { key:"partother",          label:"Participation Other", flip:true,
    help:"Other amounts settled with participants. Shown inverted: in parentheses means we paid it out to them." },
  { key:"chargeoff",          label:"Charge-Off",       walk:true, flip:true,
    help:"Charge-offs with status Actual, by as-of date. Reduces the balance." },
  { key:"chargeoffest",       label:"CO Forecast", flip:true,
    help:"Charge-offs with status Forecast — a loss expected but not yet taken. Shown in parentheses like the actual, since both are losses. Never included in the balance; it is netted off in the column at the far right instead." },
];

// ── the participation-income group ────────────────────────────────────────
// Interest, fee and other settled with participants are three columns that
// almost always get read as one number. Collapsed by default so the matrix
// fits a screen; the detail is one click away and the total stays on screen
// beside it when open, so the parts can be checked against the sum.
//
// Participation PRIN is deliberately NOT in the group. It is the one
// participation leg that moves the balance, and burying it inside a collapsed
// column would hide a walk component.
// Industry names run long ("Wholesale Trade - Durable Goods") and are the
// least-read column in the account table, so they are clamped and the full
// value moves to the tooltip. ~60% of what the longest name would otherwise
// demand, which buys back roughly two money columns.
// Fixed widths for the columns that can hold several values. They truncate
// with an ellipsis and put the full contents in the tooltip, so a borrower
// with four facilities cannot stretch the table until the money columns fall
// off the right-hand edge.
const W = { abl:78, status:104, industry:110, facility:150, state:64 };

// ── weighted average over a set of loan rows ──────────────────────────────
// Weighted by BS Loan Balance, the basis Stratification uses and for the same
// reason: a portfolio rate should describe what we actually hold, not what the
// borrower owes. On a participation we lead, the slice we sold down should not
// pull the average toward its rate.
//
// A loan with no BS balance leaves BOTH sides of the fraction, rather than
// entering the numerator at zero — otherwise a paid-off loan would drag the
// average down while contributing nothing. When nothing is left, the answer is
// null and the cell shows a dash. Not 0.00%, which reads as a real rate.
const wavg = (rows, field) => {
  let base = 0, acc = 0;
  rows.forEach(r => {
    const w = num(r.bsloanbalanceeod);
    if (!w) return;
    base += w;
    acc  += num(r[field]) * w;
  });
  return base ? acc / base : null;
};

const PART_DETAIL = ["partint", "partfee", "partother"];
const PART_GROUP = {
  key:"partincome", label:"Participation Income", flip:true, group:true,
  help:"Participation Int, Fee and Other summed. Click the header to open the three underlying columns; the total stays visible beside them. Participation Prin is not included here — it moves the balance and keeps its own column." };

const BAL_COLS = [
  { key:"balance",    label:"BS Loan Bal Outstanding",
    help:"Running total of the five tinted columns, from the portfolio's first funding. This is the balance-sheet balance — what the borrower owes less what has been settled with participants — and it equals the sum of BS Loan Balance across the loans in view. The borrower's gross balance is on its own card above; it is not walked to here." },
  { key:"balanceNet", label:"Bal net of CO Forecast",
    help:"Ending balance less the cumulative forecast charge-offs still outstanding." },
];

const ALL_COLS = [...COLS, PART_GROUP, ...BAL_COLS];

// ── the balance walk ──────────────────────────────────────────────────────
// One place. Every total, every chart point and the tie-out all read from it,
// so there is no second implementation to drift.
const delta = r => num(r.initialfundings) + num(r.draws) - num(r.receivedprin)
                 + num(r.partprin) - num(r.chargeoff);

// ── THE GROSS WALK ────────────────────────────────────────────────────────
// delta() minus the participation leg. Written as a subtraction from delta()
// rather than as its own five-term sum so the two can never disagree about
// what a funding is — add a component to the walk and this follows for free.
//
//   delta()      -> BS Loan Balance   (what we hold, after settlements)
//   deltaGross() -> Loan Balance      (what the borrower owes)
const deltaGross = r => delta(r) - num(r.partprin);

// Whole dollars, ledger-style negatives. A matrix fifty columns wide cannot
// afford cents, and at portfolio scale they are noise — the cent-accurate
// figures are in the month matrix above and in the CSV.
const fmt0 = n => {
  const v = Math.round(num(n));
  const s = "$" + Math.abs(v).toLocaleString("en-US");
  return v < 0 ? "(" + s + ")" : s;
};

// Cash-flow columns for the by-account breakdown, in the order money moves:
// what went out, what came back, then what was settled with participants.
// Keys only — the column objects are looked up in COLS so labels, help text
// and the sign-flip convention are defined exactly once for the whole page.
const CF_KEYS = ["initialfundings", "draws",
                 "receivedprin", "receivedint", "receivedfee", "receivedother",
                 "partprin", "partint", "partfee", "partother"];

export const PortfolioBalancePage = () => {
  const { api } = useApp();
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState("");
  const [busy, setBusy]   = useState(true);

  const [company, setCompany]   = useState("");
  const [status, setStatus]     = useState("");
  const [facility, setFacility] = useState("");
  const [abl, setAbl]           = useState("");
  const [fund, setFund]         = useState("");
  const [q, setQ]               = useState("");
  const [open, setOpen]         = useState({});   // expanded years
  const [showRestated, setShowRestated] = useState(false);
  // Shared by both period matrices. Two views of one series — a reader who
  // opens an account to see its facilities in the balance table means the
  // same thing in the transaction table, and having to open it twice would
  // just be two chances to compare the wrong rows.
  const [grain, setGrain] = useState("month");
  const [basis, setBasis] = useState("bs");
  const [openAcct, setOpenAcct] = useState({});
  const [openPart, setOpenPart] = useState(false);

  useEffect(() => {
    let dead = false;
    setBusy(true); setErr("");
    api.get("getPortfolioBalance")
      .then(d => { if (!dead) { setData(d); setBusy(false); } })
      .catch(e => { if (!dead) { setErr(e.message); setBusy(false); } });
    return () => { dead = true; };
  }, [api]);

  // FUNDED ONLY. A loan with no funded term has never put money out, so it
  // contributes nothing to any column here — but it would still swell the
  // account count and appear as a row of dashes. originalfundingdate is the
  // reporting layer's own record of the first funded term, so this is the same
  // definition of "funded" the rest of the app uses rather than a new one.
  //
  // Applied at the source, not just in the table: the walk, the filters, the
  // KPI cards and the tie-out all read from this list, and a universe that
  // differed between them would be the kind of discrepancy nobody can find.
  const loans = useMemo(
    () => ((data && data.loans) || []).filter(l => l.originalfundingdate),
    [data]);
  const rows  = useMemo(() => (data && data.rows)  || [], [data]);

  // fundnames is a STRING_AGG: one loan can sit in several funds, so the fund
  // filter tests membership rather than matching the whole string.
  const fundsOf = l => {
    const raw = l.fundnames;
    if (raw === null || raw === undefined || String(raw).trim() === "") return [];
    return String(raw).split(",").map(x => x.trim()).filter(Boolean);
  };

  const opts = useMemo(() => {
    const uniq = f => [...new Set(loans.map(f).filter(v => v !== null && v !== undefined && String(v).trim() !== ""))]
                       .sort().map(v => ({ value:String(v), label:String(v) }));
    const fundSet = new Set();
    loans.forEach(l => fundsOf(l).forEach(f => fundSet.add(f)));
    return {
      company:  [...new Set(loans.map(l => l.companyname).filter(Boolean))].sort()
                  .map(v => ({ value:v, label:v })),
      status:   uniq(l => l.loanstatus),
      facility: uniq(l => l.facilitytype),
      abl:      uniq(l => l.collateraltype),
      fund:     [...fundSet].sort().map(v => ({ value:v, label:v })),
    };
  }, [loans]);

  // ── the loans in view ───────────────────────────────────────────────────
  const shown = useMemo(() => {
    let ls = loans;
    if (company)  ls = ls.filter(l => l.companyname === company);
    if (status)   ls = ls.filter(l => l.loanstatus === status);
    if (facility) ls = ls.filter(l => l.facilitytype === facility);
    if (abl)      ls = ls.filter(l => l.collateraltype === abl);
    if (fund)     ls = ls.filter(l => fundsOf(l).includes(fund));
    return localFilter(ls, q, ["companyname","loannumber","state","industry",
                               "facilitytype","loanstatus","fundnames"]);
  }, [loans, company, status, facility, abl, fund, q]);

  const shownIds = useMemo(() => new Set(shown.map(l => String(l.id))), [shown]);

  // ── month rows for the loans in view, walked from inception ─────────────
  const months = useMemo(() => {
    const by = {};
    rows.forEach(r => {
      if (!shownIds.has(String(r.loanid))) return;
      const m = by[r.ym] = by[r.ym] || { ym:r.ym };
      COLS.forEach(c => { m[c.key] = num(m[c.key]) + num(r[c.key]); });
      m.uncaptured = num(m.uncaptured) + num(r.uncaptured);
    });
    const out = Object.values(by).sort((a, b) => a.ym < b.ym ? -1 : a.ym > b.ym ? 1 : 0);
    let bal = 0, est = 0;
    out.forEach(m => {
      // Derived after the row loop, not inside it: summing a sum per row would
      // be the same answer by luck, and wrong the moment a child column gains
      // its own aggregation rule.
      m.partincome = num(m.partint) + num(m.partfee) + num(m.partother);
      bal += delta(m);
      est += num(m.chargeoffest);
      m.balance    = bal;
      m.balanceNet = bal - est;
      m.coEstCum   = est;
    });
    return out;
  }, [rows, shownIds]);

  // ── year grouping ───────────────────────────────────────────────────────
  // A year's flow columns are the sum of its months. Its BALANCE is the LAST
  // month's balance, not a sum — adding twelve balances together produces a
  // number with no meaning, and it is the classic way a roll-forward goes
  // wrong.
  const years = useMemo(() => {
    const by = {};
    months.forEach(m => {
      const y = yearOf(m.ym);
      const e = by[y] = by[y] || { year:y, months:[] };
      e.months.push(m);
    });
    return Object.values(by).sort((a, b) => a.year < b.year ? -1 : 1).map(y => {
      const agg = { year:y.year, months:y.months };
      ALL_COLS.forEach(c => { agg[c.key] = 0; });
      y.months.forEach(m => COLS.forEach(c => { agg[c.key] = num(agg[c.key]) + num(m[c.key]); }));
      agg.partincome = num(agg.partint) + num(agg.partfee) + num(agg.partother);
      const last = y.months[y.months.length - 1];
      agg.balance    = last ? last.balance    : 0;
      agg.balanceNet = last ? last.balanceNet : 0;
      return agg;
    });
  }, [months]);

  const grand = useMemo(() => {
    const g = { uncaptured: 0 };
    ALL_COLS.forEach(c => { g[c.key] = 0; });
    months.forEach(m => {
      COLS.forEach(c => { g[c.key] = num(g[c.key]) + num(m[c.key]); });
      g.uncaptured += num(m.uncaptured);
    });
    g.partincome = num(g.partint) + num(g.partfee) + num(g.partother);
    const last = months[months.length - 1];
    g.balance    = last ? last.balance    : 0;
    g.balanceNet = last ? last.balanceNet : 0;
    return g;
  }, [months]);

  // ── THE TIE-OUT ─────────────────────────────────────────────────────────
  // The walk and vReportLoans compute the same figure from the same parts. If
  // they disagree, one of them is wrong and every number on this page is
  // suspect, so it is checked rather than assumed.
  const tie = useMemo(() => {
    const target = shown.reduce((s, l) => s + num(l.bsloanbalanceeod), 0);
    const walked = grand.balance;
    return { target, walked, diff: walked - target, ok: Math.abs(walked - target) < 0.5 };
  }, [shown, grand]);

  // Weighted over the LOANS in view, not an average of the account
  // averages — that would weight a one-loan account the same as a
  // ten-loan one and would not agree with the column above it.
  const grandWaMargin = useMemo(() => wavg(shown, "margin"), [shown]);

  // The two balance totals, both straight off the loan rows.
  //
  // grandBs is what the walk reproduces, so the two are checked against each
  // other by the tie-out above. grandLoanBal is the borrower's gross balance,
  // which the walk does NOT produce — the walk nets out participation
  // principal — so it has to be summed here rather than read off the matrix.
  const grandLoanBal = useMemo(
    () => shown.reduce((s, l) => s + num(l.loanbalanceeod), 0), [shown]);
  const grandBs = useMemo(
    () => shown.reduce((s, l) => s + num(l.bsloanbalanceeod), 0), [shown]);

  // The group column always renders; its three children slot in behind it only
  // when opened. Keeping the total on screen while the parts are showing is
  // what makes the detail checkable rather than just longer.
  // ── the account roll-up ─────────────────────────────────────────────────
  // A borrower can hold several facilities, and the question "who do we have
  // money out to" is asked about the borrower, not the facility. Loan-level
  // attributes are collapsed rather than dropped: one distinct value shows
  // itself, several show a count, so a single-facility account reads exactly
  // as it did before and a multi-facility one says so instead of picking one
  // of its values arbitrarily.
  const accounts = useMemo(() => {
    // Distinct values, listed. Two loans both on Accrual read "Accrual", not
    // "2 statuses" — the count was accurate and useless, because the thing a
    // reader wants is WHICH. Trimmed before de-duping so "ABL" and "ABL " do
    // not survive as two, and sorted so the order does not depend on which
    // loan happened to come back first.
    const one = vals => {
      const set = [...new Set(vals.map(v => (v === null || v === undefined)
                                            ? "" : String(v).trim())
                                  .filter(Boolean))].sort();
      return set.length ? set.join(", ") : "—";
    };
    const by = {};
    shown.forEach(l => {
      const k = l.accountnumber || l.companyname || String(l.id);
      (by[k] = by[k] || { key:k, companyname:l.companyname, rows:[] }).rows.push(l);
    });
    return Object.values(by).map(a => ({
      key:            a.key,
      companyname:    a.companyname,
      loans:          a.rows.length,
      collateraltype: one(a.rows.map(l => l.collateraltype)),
      loanstatus:     one(a.rows.map(l => l.loanstatus)),
      industry:       one(a.rows.map(l => l.industry)),
      facilitytype:   one(a.rows.map(l => l.facilitytype)),
      state:          one(a.rows.map(l => l.state)),
      // The LATEST maturity on the account — when the relationship finally
      // runs off, assuming nothing is renewed. Max, not min: the earliest
      // date would say when the first facility matures, which tells you
      // nothing about how long the exposure lasts.
      //
      // Compared as YYYY-MM-DD strings, which sort correctly and sidestep the
      // timezone shift that parsing a SQL date into a JS Date introduces.
      maturity:       a.rows.map(l => (l.maturitydate || "").slice(0, 10))
                            .filter(Boolean).sort().pop() || null,
      // acctcommitment, not commitmentamount. The two differ on loans paid off
      // at the loan level; this one is getAccounts' rule, so the column ties
      // to the Accounts page.
      commitment:     a.rows.reduce((s, l) => s + num(l.acctcommitment), 0),
      // Our share of the commitment: (1 - participationpct) where we lead a
      // participation, the whole thing otherwise. Sits between the two
      // balances because that is where it is read: gross commitment, gross
      // balance, our commitment, our balance.
      bsCommitment:   a.rows.reduce((s, l) => s + num(l.bsacctcommitment), 0),
      waMargin:       wavg(a.rows, "margin"),
      loanBal:        a.rows.reduce((s, l) => s + num(l.loanbalanceeod), 0),
      bs:             a.rows.reduce((s, l) => s + num(l.bsloanbalanceeod), 0),
    })).sort((x, y) => y.bs - x.bs);
  }, [shown]);

  // ── PERIOD GRAIN AND BALANCE BASIS ──────────────────────────────────────
  // Fifty-odd months is a lot of columns. Year rolls the same numbers up
  // without changing any of them: a period's MOVEMENT is the sum of its
  // months, its BALANCE is the last month's balance. Summing balances is the
  // classic way a roll-forward goes wrong, so the two are accumulated
  // separately rather than one being derived from the other.
  const periodOf = ym => (grain === "month" ? ym : ym.slice(0, 4));
  const periods = useMemo(() => {
    const ms = months.map(m => m.ym);
    return grain === "month" ? ms : [...new Set(ms.map(ym => ym.slice(0, 4)))];
  }, [months, grain]);

  // ── loan x month lookup ─────────────────────────────────────────────────
  const byLoanMonth = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      if (!shownIds.has(String(r.loanid))) return;
      (m[String(r.loanid)] = m[String(r.loanid)] || {})[r.ym] = r;
    });
    return m;
  }, [rows, shownIds]);

  // ── per-loan balance series ─────────────────────────────────────────────
  // Walked across the FULL month spine, not just the months a loan has rows
  // in. A quiet month still carries the balance forward — a matrix that
  // skipped it would show a gap where the exposure was unchanged, which reads
  // as "paid off" to anyone scanning the row.
  //
  // `live` marks the periods from the loan's first funded month onward. Before
  // that the cell is blank rather than $0: a zero says "we had nothing out",
  // which is true but indistinguishable from a loan that has paid off, and the
  // difference is the whole point of the matrix.
  const series = useMemo(() => {
    const d = basis === "bs" ? delta : deltaGross;
    const out = {};
    shown.forEach(l => {
      const byYm = byLoanMonth[String(l.id)] || {};
      const bals = {}, movs = {}, live = {};
      let bal = 0, started = false;
      months.forEach(m => {
        const r = byYm[m.ym];
        const mv = r ? d(r) : 0;
        if (r) started = true;
        bal += mv;
        const p = periodOf(m.ym);
        movs[p] = num(movs[p]) + mv;
        bals[p] = bal;                       // last month in the period wins
        live[p] = live[p] || started;
      });
      out[String(l.id)] = { bals, movs, live };
    });
    return out;
  }, [shown, byLoanMonth, months, basis, grain]);

  // ── account groups, with their loans kept ───────────────────────────────
  // Separate from `accounts` above, which collapses its loan rows away. The
  // matrices need the children to expand into, so the grouping is done again
  // rather than the other one being widened — the two answer different
  // questions and neither should carry the other's baggage.
  const matrix = useMemo(() => {
    const by = {};
    shown.forEach(l => {
      const k = l.accountnumber || l.companyname || String(l.id);
      (by[k] = by[k] || { key:k, companyname:l.companyname, loans:[] }).loans.push(l);
    });
    const groups = Object.values(by).map(a => {
      const bals = {}, movs = {}, live = {};
      periods.forEach(p => {
        let b = 0, mv = 0, lv = false;
        a.loans.forEach(l => {
          const s = series[String(l.id)];
          if (!s) return;
          b  += num(s.bals[p]);
          mv += num(s.movs[p]);
          lv = lv || !!s.live[p];
        });
        bals[p] = b; movs[p] = mv; live[p] = lv;
      });
      return { ...a, bals, movs, live,
               loans: [...a.loans].sort((x, y) =>
                 num(series[String(y.id)] && series[String(y.id)].bals[periods[periods.length - 1]])
                 - num(series[String(x.id)] && series[String(x.id)].bals[periods[periods.length - 1]])) };
    });
    const last = periods[periods.length - 1];
    return groups.sort((x, y) => num(y.bals[last]) - num(x.bals[last]));
  }, [shown, series, periods]);

  // Column totals, computed across the groups rather than by re-walking the
  // loans, so the footer cannot disagree with the rows above it.
  const matrixTotals = useMemo(() => {
    const bals = {}, movs = {};
    periods.forEach(p => {
      bals[p] = matrix.reduce((s, g) => s + num(g.bals[p]), 0);
      movs[p] = matrix.reduce((s, g) => s + num(g.movs[p]), 0);
    });
    return { bals, movs };
  }, [matrix, periods]);

  // ── cash flows by account ───────────────────────────────────────────────
  // Every month collapsed into one figure per loan, then rolled to the
  // account. The month matrix at the top of the page answers "when"; this
  // answers "who", off the same rows.
  const cashflows = useMemo(() => {
    const perLoan = {};
    rows.forEach(r => {
      if (!shownIds.has(String(r.loanid))) return;
      const e = perLoan[String(r.loanid)] = perLoan[String(r.loanid)] || {};
      CF_KEYS.forEach(k => { e[k] = num(e[k]) + num(r[k]); });
    });
    const by = {};
    shown.forEach(l => {
      const k = l.accountnumber || l.companyname || String(l.id);
      const g = by[k] = by[k] || { key:k, companyname:l.companyname, loans:[], tot:{} };
      const t = perLoan[String(l.id)] || {};
      g.loans.push({ ...l, cf:t });
      CF_KEYS.forEach(k2 => { g.tot[k2] = num(g.tot[k2]) + num(t[k2]); });
    });
    return Object.values(by)
      .map(g => ({ ...g, loans: [...g.loans].sort((x, y) =>
        num(y.cf.initialfundings) - num(x.cf.initialfundings)) }))
      .sort((x, y) => num(y.tot.initialfundings) - num(x.tot.initialfundings));
  }, [rows, shownIds, shown]);

  const cashflowTotals = useMemo(() => {
    const t = {};
    CF_KEYS.forEach(k => { t[k] = cashflows.reduce((s, g) => s + num(g.tot[k]), 0); });
    return t;
  }, [cashflows]);

  const toggleAcct = k => setOpenAcct(o => ({ ...o, [k]: !o[k] }));

  // ── exports ─────────────────────────────────────────────────────────────
  // One row per account AND one per loan, with a level column, so the file
  // carries the same detail the expanded table shows rather than only what
  // happened to be open when the button was pressed.
  const matrixCsv = (which, name) => {
    const flat = [];
    const push = (level, label, o) =>
      flat.push({ level, name:label,
                  ...Object.fromEntries(periods.map(p =>
                    [p, o.live[p] ? Math.round(num(o[which][p])) : ""])) });
    matrix.forEach(g => {
      push("account", g.companyname, g);
      g.loans.forEach(l => {
        const s = series[String(l.id)];
        if (s) push("loan", g.companyname + " / " + (l.loannumber || l.id), s);
      });
    });
    flat.push({ level:"total", name:"Total",
                ...Object.fromEntries(periods.map(p =>
                  [p, Math.round(num(matrixTotals[which][p]))])) });
    exportCSV(flat, ["level", "name", ...periods], name);
  };

  const cashflowCsv = () => {
    const flat = [];
    const pick2 = o => Object.fromEntries(CF_KEYS.map(k => {
      const c = COLS.find(x => x.key === k);
      return [k, c && c.flip ? -num(o[k]) : num(o[k])];
    }));
    cashflows.forEach(g => {
      flat.push({ level:"account", name:g.companyname, ...pick2(g.tot) });
      g.loans.forEach(l => flat.push({
        level:"loan", name:g.companyname + " / " + (l.loannumber || l.id), ...pick2(l.cf) }));
    });
    flat.push({ level:"total", name:"Total", ...pick2(cashflowTotals) });
    exportCSV(flat, ["level", "name", ...CF_KEYS], "portfolio_cashflows.csv");
  };

  // The denominator for "n of m" — every funded account, before any filter.
  const allAccountCount = useMemo(
    () => new Set(loans.map(l => l.accountnumber || l.companyname || String(l.id))).size,
    [loans]);

  const visibleCols = useMemo(() => {
    const out = [];
    COLS.forEach(c => {
      if (c.key === "restatedcommitment" && !showRestated) return;
      if (c.key === PART_DETAIL[0]) { out.push(PART_GROUP); if (openPart) out.push(c); return; }
      if (PART_DETAIL.includes(c.key)) { if (openPart) out.push(c); return; }
      out.push(c);
    });
    return out;
  }, [showRestated, openPart]);

  const toggleYear = y => setOpen(o => ({ ...o, [y]: !o[y] }));

  const csv = () => {
    const flat = [];
    years.forEach(y => {
      flat.push({ period:y.year, ...pick(y) });
      y.months.forEach(m => flat.push({ period:y.year + "-" + m.ym.slice(5), ...pick(m) }));
    });
    flat.push({ period:"Total", ...pick(grand) });
    exportCSV(flat, ["period", ...ALL_COLS.map(c => c.key)], "portfolio_balance.csv");
  };
  // The export carries the DISPLAYED sign, not the stored one. A CSV whose
  // participation columns disagree with the screen is a reconciliation trap:
  // both are defensible in isolation and they differ by a factor of -1.
  const pick = o => {
    const r = {};
    ALL_COLS.forEach(c => { r[c.key] = c.flip ? -num(o[c.key]) : num(o[c.key]); });
    return r;
  };

  if (busy) return <Spinner />;
  if (err)  return <ErrorMsg msg={err} />;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* ── ribbon ─────────────────────────────────────────────────────── */}
      <Card style={{ padding:"12px 14px" }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12, alignItems:"flex-end" }}>
          <FilterSelect label="Company"  value={company}  onChange={setCompany}
                        options={opts.company}  allLabel="All companies" />
          <FilterSelect label="Status"   value={status}   onChange={setStatus}
                        options={opts.status}   allLabel="All statuses" />
          <FilterSelect label="Facility" value={facility} onChange={setFacility}
                        options={opts.facility} allLabel="All facilities" />
          <FilterSelect label="ABL/CF"   value={abl}      onChange={setAbl}
                        options={opts.abl}      allLabel="All types" />
          <FilterSelect label="Fund"     value={fund}     onChange={setFund}
                        options={opts.fund}     allLabel="All funds"
                        disabled={!opts.fund.length} />
          <SearchBar value={q} onChange={setQ} placeholder="Search loans, states, industries..." />
          <label style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12,
                          color:INK_MUTED, whiteSpace:"nowrap", paddingBottom:2 }}>
            <input type="checkbox" checked={showRestated}
                   onChange={e => setShowRestated(e.target.checked)} />
            Show restated commitment
          </label>
          <div style={{ marginLeft:"auto" }}>
            <Btn onClick={csv}>Export CSV</Btn>
          </div>
        </div>
      </Card>

      {/* ── headline + tie-out ─────────────────────────────────────────── */}
      <div style={{ display:"grid", gap:12,
                    gridTemplateColumns:"repeat(auto-fit, minmax(190px, 1fr))" }}>
        {/* Left to right, the order a loan actually moves through: what was
            committed, what went out, what came back in interest, what came
            back in principal, what the borrower still owes, and how much of
            that is still ours. The last two are a pair, and the gap between
            them is participation principal already settled. */}
        <Stat label="New Commitments" value={fmt(grand.newcommitment)}
              note={`${fmt(grand.newloancommitment)} from new loans`} />
        <Stat label="Initial Fundings" value={fmt(grand.initialfundings)}
              note={`${fmt(grand.draws)} draws funded`} />
        <Stat label="Interest Received" value={fmt(grand.receivedint)}
              note={partNote(grand.partint)} />
        <Stat label="Principal Received" value={fmt(grand.receivedprin)}
              note={partNote(grand.partprin)} />
        <Stat label="Loan Bal Outstanding" value={fmt(grandLoanBal)}
              note={`${accounts.length} account${accounts.length === 1 ? "" : "s"}`
                    + ` \u00b7 ${shown.length} loan${shown.length === 1 ? "" : "s"}`} />
        {/* This is the one the matrix walks to. The note says how much of
            the borrower balance has already been settled with participants,
            since that difference is the only reason the two cards differ. */}
        <Stat label="BS Loan Bal Outstanding" value={fmt(grandBs)}
              note={grandLoanBal
                    ? `${(grandBs / grandLoanBal * 100).toFixed(1)}% of loan balance`
                      + ` \u00b7 ${fmt(grandLoanBal - grandBs)} settled`
                    : "\u2014"} />
      </div>

      {/* The walk and the reporting view reach the same number by different
          routes, so a gap means one of them is missing rows. Silent while they
          agree — which is every normal render — and loud when they do not,
          because every figure on the page depends on it. */}
      {!tie.ok && (
        <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"9px 13px",
                      borderRadius:8, background:SURFACE_MUTED, fontSize:12, lineHeight:1.6,
                      color:INK_PRIMARY, borderLeft:"3px solid " + B.amberDark }}>
          <span style={{ fontSize:13, lineHeight:1.3 }} aria-hidden="true">{"\u26a0"}</span>
          <span>
            <strong>Does not tie — {fmt(Math.abs(tie.diff))} {tie.diff > 0 ? "over" : "under"}.</strong>{" "}
            The walk ends at {fmt(tie.walked)}; BS Loan Balance sums to {fmt(tie.target)}.
            These are built from the same components, so a gap means one side is
            missing rows — most likely a payment, draw or charge-off dated outside
            its loan's own history. Treat every figure here as unverified until it
            is resolved.
          </span>
        </div>
      )}

      {/* ── the chart ──────────────────────────────────────────────────── */}
      <ChartPanel months={months} />

      {/* ── the matrix ─────────────────────────────────────────────────── */}
      <Card style={{ padding:0, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12,
                          fontVariantNumeric:"tabular-nums" }}>
            <thead>
              <tr>
                <Hd sticky>Period</Hd>
                {visibleCols.map(c => (
                  <Hd key={c.key} num walk={c.walk} dim={c.dim} help={c.help}
                      child={PART_DETAIL.includes(c.key)}
                      onToggle={c.group ? () => setOpenPart(o => !o) : null}
                      open={c.group ? openPart : undefined}>
                    {c.label}
                  </Hd>
                ))}
                {BAL_COLS.map(c => <Hd key={c.key} num bal help={c.help}>{c.label}</Hd>)}
              </tr>
            </thead>
            <tbody>
              {years.map(y => (
                <Fragment key={y.year}>
                  <Row o={y} cols={visibleCols} bold
                       label={
                         <button onClick={() => toggleYear(y.year)}
                                 style={{ background:"none", border:"none", padding:0, cursor:"pointer",
                                          font:"inherit", fontWeight:700, color:INK_PRIMARY,
                                          display:"inline-flex", alignItems:"center", gap:6 }}>
                           <span style={{ fontSize:9, color:INK_MUTED, width:8, display:"inline-block" }}>
                             {open[y.year] ? "▼" : "▶"}
                           </span>
                           {y.year}
                         </button>
                       } />
                  {open[y.year] && y.months.map(m => (
                    <Row key={m.ym} o={m} cols={visibleCols} indent
                         label={monthLabel(m.ym)} />
                  ))}
                </Fragment>
              ))}
              <Row o={grand} cols={visibleCols} total label="Total" />
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── loan detail ────────────────────────────────────────────────── */}
      <Card style={{ padding:0, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px 0" }}>
          <SectionTitle>
            Accounts in view
            <span style={{ fontWeight:400, color:INK_MUTED, marginLeft:8 }}>
              {accounts.length} of {allAccountCount}
              {" · "}{shown.length} loan{shown.length === 1 ? "" : "s"}
            </span>
          </SectionTitle>
        </div>
        {/* Tall enough that a normal book needs no scrolling at all. It is the last thing on the page, so the height costs nothing above it — and a short window on a table people scan account by account just means more scrolling for no gain. */}
        <div style={{ overflowX:"auto", maxHeight:"72vh" }}>
          <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12,
                          fontVariantNumeric:"tabular-nums" }}>
            <thead>
              <tr>
                {["Company","Loans","ABL/CF","Status","Industry","Facility","Maturity","State"]
                  .map(h => <Hd key={h} sticky>{h}</Hd>)}
                {["WA Margin","% of Bal","Commitment","Loan Balance","BS Commitment","BS Loan Balance"]
                  .map(h => <Hd key={h} num sticky>{h}</Hd>)}
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.key} style={{ borderTop:"1px solid " + HAIRLINE }}>
                  <Cl>{a.companyname}</Cl>
                  <Cl num>{a.loans}</Cl>
                  <Cl cap={W.abl}      title={a.collateraltype}>{a.collateraltype}</Cl>
                  <Cl cap={W.status}   title={a.loanstatus}>{a.loanstatus}</Cl>
                  <Cl cap={W.industry} title={a.industry}>{a.industry}</Cl>
                  <Cl cap={W.facility} title={a.facilitytype}>{a.facilitytype}</Cl>
                  <Cl>{a.maturity ? fmtDate(a.maturity) : "—"}</Cl>
                  <Cl cap={W.state}    title={a.state}>{a.state}</Cl>
                  <Cl num>{a.waMargin === null ? "\u2014" : fmtRatio(a.waMargin)}</Cl>
                  <Cl num>{grandBs ? (a.bs / grandBs * 100).toFixed(1) + "%" : "\u2014"}</Cl>
                  <Cl num>{money(a.commitment)}</Cl>
                  <Cl num>{money(a.loanBal)}</Cl>
                  <Cl num>{money(a.bsCommitment)}</Cl>
                  <Cl num>{money(a.bs)}</Cl>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop:"2px solid " + HAIRLINE_FIRM, fontWeight:700,
                           background:SURFACE_MUTED }}>
                <Cl>Total</Cl>
                <Cl num>{shown.length}</Cl>
                <Cl /><Cl /><Cl /><Cl /><Cl /><Cl />
                <Cl num>{grandWaMargin === null ? "\u2014" : fmtRatio(grandWaMargin)}</Cl>
                <Cl num>100.0%</Cl>
                <Cl num>{fmt(accounts.reduce((s, a) => s + a.commitment, 0))}</Cl>
                <Cl num>{fmt(accounts.reduce((s, a) => s + a.loanBal, 0))}</Cl>
                <Cl num>{fmt(accounts.reduce((s, a) => s + a.bsCommitment, 0))}</Cl>
                <Cl num>{fmt(grandBs)}</Cl>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
      {/* ── balance outstanding, by period ─────────────────────────────── */}
      <Card style={{ padding:0, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px 10px", display:"flex", gap:12,
                      alignItems:"center", flexWrap:"wrap" }}>
          <SectionTitle>
            {basis === "bs" ? "BS Loan Balance" : "Loan Balance"} Outstanding
            <span style={{ fontWeight:400, color:INK_MUTED, marginLeft:8,
                           textTransform:"none", letterSpacing:0 }}>
              at each period end · {matrix.length} account{matrix.length === 1 ? "" : "s"}
            </span>
          </SectionTitle>
          <div style={{ display:"flex", gap:8, marginLeft:"auto", alignItems:"center" }}>
            <Seg value={basis} onChange={setBasis} options={[
              { value:"bs",    label:"BS Loan Bal",
                help:"What we hold: the borrower's balance less principal already settled with participants. This is the figure the walk above reproduces." },
              { value:"gross", label:"Loan Bal",
                help:"What the borrower owes, before any participation settlement." },
            ]} />
            <Seg value={grain} onChange={setGrain} options={[
              { value:"month", label:"Month" },
              { value:"year",  label:"Year", help:"Same numbers rolled up. A period's balance is its last month's balance, never a sum of months." },
            ]} />
            <Btn onClick={() => setOpenAcct(
              Object.keys(openAcct).length ? {} :
              Object.fromEntries(matrix.filter(g => g.loans.length > 1).map(g => [g.key, true])))}>
              {Object.keys(openAcct).length ? "Collapse all" : "Expand all"}
            </Btn>
            <Btn onClick={() => matrixCsv("bals", "portfolio_balance_outstanding.csv")}>Export CSV</Btn>
          </div>
        </div>
        {/* Click an account with more than one facility to break it out. A
            single-facility account has nothing to expand into, so it does not
            offer a caret rather than opening onto a copy of itself. */}
        <Matrix periods={periods} groups={matrix} totals={matrixTotals}
                which="bals" series={series} open={openAcct} onToggle={toggleAcct} />
      </Card>

      {/* ── balance transactions, by period ────────────────────────────── */}
      <Card style={{ padding:0, overflow:"hidden" }}>
        {/* Title and formula on the left, export on the right — the same
            shape as the other two cards. An export button that moves from card
            to card is a small thing that costs a hunt every time. */}
        <div style={{ padding:"12px 16px 10px", display:"flex", gap:12,
                      alignItems:"flex-start", flexWrap:"wrap" }}>
          <div style={{ flex:"1 1 420px", minWidth:0 }}>
          <SectionTitle>
            {basis === "bs" ? "BS Loan Balance" : "Loan Balance"} Transactions
            <span style={{ fontWeight:400, color:INK_MUTED, marginLeft:8,
                           textTransform:"none", letterSpacing:0 }}>
              what moved the balance, by period
            </span>
          </SectionTitle>
          {/* Saying which components are in the number matters more than it
              looks: the difference between the two bases is exactly the
              participation leg, and a reader comparing this table to the one
              above needs to know that is the only thing that changed. */}
          <div style={{ fontSize:11.5, color:INK_MUTED, lineHeight:1.55, maxWidth:760 }}>
            Initial Fundings + Draws − Received Principal
            {basis === "bs" && <> + Participation Principal</>} − Charge-Offs.
            Each cell is the change in the balance above it, off the same
            accumulator, so the two tables cannot disagree.
            {basis === "gross" && <> Participation principal is excluded on this
              basis — switch to BS Loan Bal to include it.</>}
          </div>
          </div>
          <div style={{ marginLeft:"auto" }}>
            <Btn onClick={() => matrixCsv("movs", "portfolio_balance_transactions.csv")}>Export CSV</Btn>
          </div>
        </div>
        <Matrix periods={periods} groups={matrix} totals={matrixTotals}
                which="movs" series={series} open={openAcct} onToggle={toggleAcct} />
      </Card>

      {/* ── cash flows by account ──────────────────────────────────────── */}
      <Card style={{ padding:0, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px 10px", display:"flex", gap:12,
                      alignItems:"center", flexWrap:"wrap" }}>
          <SectionTitle>
            Cash Flows by Account
            <span style={{ fontWeight:400, color:INK_MUTED, marginLeft:8,
                           textTransform:"none", letterSpacing:0 }}>
              every period summed · {cashflows.length} account{cashflows.length === 1 ? "" : "s"}
            </span>
          </SectionTitle>
          <div style={{ marginLeft:"auto" }}>
            <Btn onClick={cashflowCsv}>Export CSV</Btn>
          </div>
        </div>
        {/* Signs follow the matrix at the top of the page, not the stored
            values: money coming in is positive, money going out is in
            parentheses. Two tables on one screen disagreeing about which way
            a participation payment points is a reconciliation trap — both
            readings are defensible in isolation and they differ by −1. */}
        <div style={{ overflow:"auto", maxHeight:"62vh" }}>
          <table style={{ borderCollapse:"collapse", fontSize:12,
                          fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" }}>
            <thead>
              <tr>
                <th style={{ ...MHEAD, position:"sticky", left:0, top:0, zIndex:3,
                             background:SURFACE, textAlign:"left", minWidth:230,
                             borderRight:"1px solid " + HAIRLINE }}>Company</th>
                {CF_KEYS.map(k => {
                  const c = COLS.find(x => x.key === k);
                  return (
                    <th key={k} title={c && c.help}
                        style={{ ...MHEAD, position:"sticky", top:0, zIndex:2,
                                 background:SURFACE, textAlign:"right",
                                 cursor:c && c.help ? "help" : undefined }}>
                      {c ? c.label : k}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {cashflows.map(g => (
                <Fragment key={g.key}>
                  <tr style={{ borderTop:"1px solid " + HAIRLINE }}>
                    <Nm bg={SURFACE} minWidth={230}
                        hasKids={g.loans.length > 1}
                        open={!!openAcct[g.key]}
                        onClick={g.loans.length > 1 ? () => toggleAcct(g.key) : undefined}>
                      {g.companyname}
                    </Nm>
                    {CF_KEYS.map(k => (
                      <td key={k} style={MNUM}>{cell(COLS.find(x => x.key === k), g.tot[k])}</td>
                    ))}
                  </tr>
                  {openAcct[g.key] && g.loans.length > 1 && g.loans.map(l => (
                    <tr key={l.id} style={{ borderTop:"1px solid " + HAIRLINE,
                                            background:SURFACE_MUTED }}>
                      <Nm bg={SURFACE_MUTED} indent minWidth={230}>
                        {l.loannumber || l.id}
                        {l.facilitytype ? " · " + l.facilitytype : ""}
                      </Nm>
                      {CF_KEYS.map(k => (
                        <td key={k} style={{ ...MNUM, color:INK_MUTED }}>
                          {cell(COLS.find(x => x.key === k), l.cf[k])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop:"2px solid " + HAIRLINE_FIRM, fontWeight:700 }}>
                <Nm bg={SURFACE_MUTED} minWidth={230}>Total</Nm>
                {CF_KEYS.map(k => (
                  <td key={k} style={{ ...MNUM, background:SURFACE_MUTED }}>
                    {cell(COLS.find(x => x.key === k), cashflowTotals[k])}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ── wide-matrix pieces ────────────────────────────────────────────────────
// These three tables are period-by-period, so they are wide by nature and the
// company column has to stay put while the months scroll. That means
// position:sticky, and sticky cells need an OPAQUE background of their own —
// sticky does not create one, so a transparent name cell lets the money
// columns slide underneath it and renders two numbers on top of each other.
// Same failure the analytics tables hit before; the fix is to pass the row's
// background down rather than to inherit it.
const MHEAD = {
  padding:"7px 10px", whiteSpace:"nowrap", fontSize:10.5, fontWeight:600,
  textTransform:"uppercase", letterSpacing:"0.04em", color:INK_PRIMARY,
  borderBottom:"1.5px solid " + HAIRLINE_FIRM,
};
const MNUM = { padding:"5px 10px", textAlign:"right", whiteSpace:"nowrap", color:INK_PRIMARY };

const Nm = ({ children, indent, bg, onClick, open, hasKids, minWidth }) => (
  <td onClick={onClick}
      style={{ position:"sticky", left:0, zIndex:1, background:bg,
               padding:"5px 10px", paddingLeft:indent ? 26 : 10,
               whiteSpace:"nowrap", textAlign:"left", minWidth,
               color: indent ? INK_MUTED : INK_PRIMARY,
               borderRight:"1px solid " + HAIRLINE,
               cursor:onClick ? "pointer" : undefined,
               userSelect:onClick ? "none" : undefined }}>
    {/* The caret's slot is ALWAYS rendered, empty when there is nothing to
        expand. Drawing it only on expandable rows left every other account
        name starting a few pixels further left, which reads as a second level
        of indentation that does not mean anything — the eye takes it for
        hierarchy. A fixed-width box costs nothing and keeps the column of
        names straight. */}
    <span style={{ display:"inline-block", width:12, fontSize:8,
                   color:INK_MUTED, textAlign:"left" }} aria-hidden="true">
      {hasKids ? (open ? "▼" : "▶") : ""}
    </span>
    {children}
  </td>
);

// A period column header. Months read "Aug 22" rather than "2022-08" — the
// axis is scanned, not parsed.
const periodLabel = p => (p.length === 4 ? p : monthLabel(p) + " " + p.slice(2, 4));

const Seg = ({ value, onChange, options }) => (
  <div style={{ display:"inline-flex", border:"1px solid " + HAIRLINE_FIRM,
                borderRadius:6, overflow:"hidden" }}>
    {options.map(o => (
      <button key={o.value} type="button" onClick={() => onChange(o.value)}
              title={o.help}
              style={{ padding:"4px 10px", fontSize:11, fontWeight:600, cursor:"pointer",
                       border:"none", borderLeft:"1px solid " + HAIRLINE,
                       background: value === o.value ? B.cyanLight : SURFACE,
                       color: value === o.value ? INK_PRIMARY : INK_MUTED }}>
        {o.label}
      </button>
    ))}
  </div>
);

// ── the period matrix ─────────────────────────────────────────────────────
// One component, two readings of the same series:
//
//   which="bals"  a STOCK. What was outstanding at each period end. Blank
//                 before a loan's first funded month — a $0 there is true but
//                 indistinguishable from a loan that has paid off, and telling
//                 those apart is most of what this table is for.
//
//   which="movs"  a FLOW. What moved in the period. A zero reads as a dash,
//                 so the months something happened stand out from the months
//                 nothing did.
//
// The two tie by construction: a period's movement is the change in the
// balance above it, because both come from the same accumulator.
const Matrix = ({ periods, groups, totals, which, series, open, onToggle }) => {
  const show = (o, p) => {
    const v = num(o[which][p]);
    if (which === "bals") return o.live[p] ? fmt0(v) : "";
    return v === 0 ? "—" : fmt0(v);
  };
  if (!periods.length) return (
    <div style={{ padding:"36px 0", textAlign:"center", color:INK_MUTED, fontSize:13 }}>
      No activity for the current filters.
    </div>
  );
  return (
    <div style={{ overflow:"auto", maxHeight:"62vh" }}>
      <table style={{ borderCollapse:"collapse", fontSize:12,
                      fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" }}>
        <thead>
          <tr>
            <th style={{ ...MHEAD, position:"sticky", left:0, top:0, zIndex:3,
                         background:SURFACE, textAlign:"left", minWidth:230,
                         borderRight:"1px solid " + HAIRLINE }}>Company</th>
            {periods.map(p => (
              <th key={p} style={{ ...MHEAD, position:"sticky", top:0, zIndex:2,
                                   background:SURFACE, textAlign:"right" }}>
                {periodLabel(p)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map(g => (
            <Fragment key={g.key}>
              <tr style={{ borderTop:"1px solid " + HAIRLINE }}>
                <Nm bg={SURFACE} minWidth={230}
                    hasKids={g.loans.length > 1}
                    open={!!open[g.key]}
                    onClick={g.loans.length > 1 ? () => onToggle(g.key) : undefined}>
                  {g.companyname}
                </Nm>
                {periods.map(p => <td key={p} style={MNUM}>{show(g, p)}</td>)}
              </tr>
              {/* Gated on the same condition as the caret. A one-facility
                  account has nothing to break out, and rendering a child
                  row identical to its parent would be noise — this way the
                  state and the affordance cannot disagree. */}
              {open[g.key] && g.loans.length > 1 && g.loans.map(l => {
                const s = series[String(l.id)];
                if (!s) return null;
                return (
                  <tr key={l.id} style={{ borderTop:"1px solid " + HAIRLINE,
                                          background:SURFACE_MUTED }}>
                    <Nm bg={SURFACE_MUTED} indent minWidth={230}>
                      {l.loannumber || l.id}
                      {l.facilitytype ? " · " + l.facilitytype : ""}
                    </Nm>
                    {periods.map(p => (
                      <td key={p} style={{ ...MNUM, color:INK_MUTED }}>{show(s, p)}</td>
                    ))}
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop:"2px solid " + HAIRLINE_FIRM, fontWeight:700 }}>
            <Nm bg={SURFACE_MUTED} minWidth={230}>Total</Nm>
            {periods.map(p => {
              const v = num(totals[which][p]);
              return (
                <td key={p} style={{ ...MNUM, background:SURFACE_MUTED }}>
                  {which === "bals" ? fmt0(v) : v === 0 ? "—" : fmt0(v)}
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ── chart ─────────────────────────────────────────────────────────────────
// Top panel: the balance as a filled line. Bottom panel, on its own zero:
// money in (fundings + draws) above, money out (principal received +
// charge-offs) below, with the month's net as a dot. The PBI version plots
// cumulative fundings alone, which rises forever and cannot show a month the
// book shrank; splitting each month by direction also shows a month that was
// big in AND out, which a single net bar would hide as a stub.
//
// Two looks, same chart: Dark for presenting the book, Light in ACE's normal
// palette. The switch is the app-wide one in shared/chartTheme.
// Colours go on through style, not presentation attributes, so the brand
// CSS variables (and any tenant override of them) resolve inside the SVG.
const PALETTES = {
  dark: {
    grid:"rgba(255,255,255,0.08)", axis:"rgba(255,255,255,0.5)", zero:"rgba(255,255,255,0.25)",
    line:"#38d6f5", area:"#38d6f5", areaOp:0.55,
    upTop:"#5ee7ff", upBot:"#0f8fb3", dnTop:"#9a5b17", dnBot:"#ffc073",
    dot:"#ffffff", netLine:null, peak:"#ffd479", label:"#ffffff",
    legend:"rgba(255,255,255,0.75)", hover:"rgba(255,255,255,0.06)",
    lineFx:"glow", halo:"#38d6f5", ring:"#ffffff",
  },
  light: {
    grid:HAIRLINE, axis:INK_MUTED, zero:HAIRLINE_FIRM,
    line:B.cyanDark, area:B.cyan, areaOp:0.30,
    upTop:B.cyan, upBot:B.cyanDark, dnTop:B.amber, dnBot:B.amberMid,
    dot:B.sidebar, netLine:B.sidebar, peak:"#b8740a", label:INK_PRIMARY,
    legend:INK_MUTED, hover:"rgba(17,101,136,0.06)",
    lineFx:"shadow", halo:B.cyan, ring:B.cyanDark,
  },
};

const ChartPanel = ({ months }) => {
  const T = useChartTheme();
  const [box, width] = useWidth();
  const mode = T.mode;
  const dark = T.dark;
  const toggle = <ChartThemeToggle />;
  const cardStyle = { padding:"18px 20px 12px", ...chartCardStyle(T) };
  return (
    <Card style={cardStyle}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:800, letterSpacing:"0.14em",
                        color: dark ? "#fff" : B.cyanDark }}>PORTFOLIO BALANCE</div>
          <div style={{ fontSize:12, marginTop:2,
                        color: dark ? "rgba(255,255,255,0.6)" : INK_MUTED }}>
            Loan balance outstanding and what moved it each month
          </div>
        </div>
        {toggle}
      </div>
      <div ref={box}><BalanceChart months={months} W={width} P={PALETTES[mode]} /></div>
    </Card>
  );
};

const BalanceChart = ({ months, W = 1000, P = PALETTES.light }) => {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  if (!months.length) return (
    <div style={{ padding:"60px 0", textAlign:"center", color:P.axis, fontSize:13 }}>
      No activity in view.
    </div>
  );
  const H = 380, PADL = 66, PADR = 24;
  const TOP = 34, TOPB = 220;          // balance panel
  const ZERO = 292, AMP = 50;          // flow panel: zero line and half-height
  const AXIS = 352;

  const ups   = months.map(m => num(m.initialfundings) + num(m.draws) + Math.max(num(m.partprin), 0));
  const downs = months.map(m => num(m.receivedprin) + num(m.chargeoff) + Math.max(-num(m.partprin), 0));
  const nets  = months.map(m => delta(m));
  const bals  = months.map(m => m.balance);

  const bMax = Math.max(1, ...bals) * 1.08;
  const fMax = Math.max(1, ...ups, ...downs);
  const step = (W - PADL - PADR) / months.length;
  const bw   = Math.max(2, Math.min(14, step * 0.6));
  const cx = i => PADL + i * step + step / 2;
  const by = v => TOPB - (Math.max(v, 0) / bMax) * (TOPB - TOP);
  const fy = v => ZERO - (v / fMax) * AMP;

  const pts  = months.map((m, i) => [cx(i), by(m.balance)]);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${cx(months.length - 1).toFixed(1)},${TOPB} L${cx(0).toFixed(1)},${TOPB} Z`;
  const netPath = nets.map((v, i) => `${i ? "L" : "M"}${cx(i).toFixed(1)},${fy(v).toFixed(1)}`).join(" ");

  let pk = 0; bals.forEach((v, i) => { if (v > bals[pk]) pk = i; });
  const last = months.length - 1;
  const [lx, ly] = pts[last];
  const [px, py] = pts[pk];
  const every = Math.ceil(months.length / Math.max(4, Math.floor((W - PADL - PADR) / 48)));
  const tip = (m, i) => `${monthLabel(m.ym)} ${yearOf(m.ym)}\n`
    + `Ending balance: ${fmt(m.balance)}\n`
    + `In (fundings + draws): ${fmt(ups[i])}\n`
    + `Out (principal + charge-offs): ${fmt(-downs[i])}\n`
    + `Net change: ${fmt(nets[i])}`;
  const peakLeft = px > W - 220;
  const k = `pb${uid}`;
  const axisTxt = { fontSize:10, fill:P.axis };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display:"block", overflow:"visible", marginTop:6 }}>
      <defs>
        <linearGradient id={`a${uid}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" style={{ stopColor:P.area, stopOpacity:P.areaOp }} />
          <stop offset="100%" style={{ stopColor:P.area, stopOpacity:0 }} />
        </linearGradient>
        <linearGradient id={`u${uid}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" style={{ stopColor:P.upTop }} /><stop offset="100%" style={{ stopColor:P.upBot }} />
        </linearGradient>
        <linearGradient id={`d${uid}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" style={{ stopColor:P.dnTop }} /><stop offset="100%" style={{ stopColor:P.dnBot }} />
        </linearGradient>
        {P.lineFx === "glow" ? (
          <filter id={`f${uid}`} x="-10%" y="-30%" width="120%" height="160%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        ) : (
          <filter id={`f${uid}`} x="-10%" y="-30%" width="120%" height="180%">
            <feDropShadow dx="0" dy="5" stdDeviation="4" floodColor="#116588" floodOpacity="0.28" />
          </filter>
        )}
      </defs>
      <style>{`
        .${k}-line{stroke-dasharray:1;stroke-dashoffset:1;animation:${k}-draw 1.6s ease-out forwards}
        .${k}-area{opacity:0;animation:${k}-fade 1.2s .5s ease-out forwards}
        .${k}-bar{transform-box:fill-box;transform:scaleY(0);animation:${k}-grow .7s ease-out forwards}
        .${k}-up{transform-origin:50% 100%}
        .${k}-dn{transform-origin:50% 0%}
        .${k}-late{opacity:0;animation:${k}-fade .5s 1.5s forwards}
        .${k}-col:hover .${k}-hl{opacity:1}
        @keyframes ${k}-draw{to{stroke-dashoffset:0}}
        @keyframes ${k}-fade{to{opacity:1}}
        @keyframes ${k}-grow{to{transform:scaleY(1)}}
        @media (prefers-reduced-motion: reduce){
          .${k}-line,.${k}-area,.${k}-bar,.${k}-late{animation:none;opacity:1;transform:none;stroke-dashoffset:0}
        }
      `}</style>

      {/* balance gridlines */}
      {[0, 0.25, 0.5, 0.75, 1].map(g => (
        <g key={g}>
          <line x1={PADL} x2={W - PADR} y1={by(g * bMax)} y2={by(g * bMax)} style={{ stroke:P.grid }} />
          <text x={PADL - 10} y={by(g * bMax) + 4} textAnchor="end" style={axisTxt}>{short(g * bMax)}</text>
        </g>
      ))}

      {/* flow panel frame */}
      <line x1={PADL} x2={W - PADR} y1={ZERO} y2={ZERO} style={{ stroke:P.zero }} />
      <text x={PADL - 10} y={ZERO + 3} textAnchor="end" style={{ ...axisTxt, fontSize:9 }}>0</text>
      <text x={PADL - 10} y={fy(fMax) + 3} textAnchor="end" style={{ ...axisTxt, fontSize:9 }}>{short(fMax)}</text>
      <text x={PADL - 10} y={fy(-fMax) + 3} textAnchor="end" style={{ ...axisTxt, fontSize:9 }}>{short(-fMax)}</text>

      {/* per-month columns: hover highlight, bars, axis label */}
      {months.map((m, i) => {
        const x = cx(i) - bw / 2;
        const uh = Math.abs(fy(ups[i]) - ZERO), dh = Math.abs(fy(-downs[i]) - ZERO);
        const delay = `${(i / months.length) * 0.8}s`;
        return (
          <g key={m.ym} className={`${k}-col`}>
            <title>{tip(m, i)}</title>
            <rect className={`${k}-hl`} x={cx(i) - step / 2} y={TOP - 6} width={step}
                  height={AXIS - TOP} style={{ fill:P.hover, opacity:0 }} />
            {uh > 0.3 && <rect className={`${k}-bar ${k}-up`} style={{ animationDelay:delay }}
                  x={x} y={ZERO - uh} width={bw} height={uh} rx={Math.min(3, bw / 2)} fill={`url(#u${uid})`} />}
            {dh > 0.3 && <rect className={`${k}-bar ${k}-dn`} style={{ animationDelay:delay }}
                  x={x} y={ZERO} width={bw} height={dh} rx={Math.min(3, bw / 2)} fill={`url(#d${uid})`} />}
            {i % every === 0 && (
              <text x={cx(i)} y={AXIS + 4} textAnchor="end"
                    transform={`rotate(-52 ${cx(i)} ${AXIS + 4})`}
                    style={{ ...axisTxt, fontSize:9 }}>
                {monthLabel(m.ym)} {yearOf(m.ym).slice(2)}
              </text>
            )}
          </g>
        );
      })}

      {/* net change: dots, joined by a thin line in the light look */}
      <g className={`${k}-late`} style={{ pointerEvents:"none" }}>
        {P.netLine && months.length > 1 && (
          <path d={netPath} fill="none" strokeWidth="1.2" strokeLinejoin="round"
                style={{ stroke:P.netLine, strokeOpacity:0.45 }} />
        )}
        {nets.map((v, i) => v !== 0 && (
          <circle key={i} cx={cx(i)} cy={fy(v)} r={Math.max(1.6, Math.min(2.6, bw / 4))}
                  style={{ fill:P.dot }} />
        ))}
      </g>

      {/* balance area + line (glow in dark, soft shadow in light) */}
      <path className={`${k}-area`} d={area} fill={`url(#a${uid})`} style={{ pointerEvents:"none" }} />
      <path className={`${k}-line`} d={line} pathLength="1" fill="none"
            strokeWidth="2.5" strokeLinejoin="round" filter={`url(#f${uid})`}
            style={{ stroke:P.line, pointerEvents:"none" }} />

      {/* peak marker */}
      {pk !== last && (
        <g className={`${k}-late`} style={{ pointerEvents:"none" }}>
          <line x1={px} x2={px} y1={py} y2={TOP - 8} strokeDasharray="2 3" style={{ stroke:P.peak, strokeOpacity:0.7 }} />
          <circle cx={px} cy={py} r="4" style={{ fill:P.peak }} />
          <text x={px + (peakLeft ? -6 : 6)} y={TOP - 10} textAnchor={peakLeft ? "end" : "start"}
                style={{ fontSize:11, fontWeight:700, fill:P.peak }}>
            Peak {short(bals[pk])} · {monthLabel(months[pk].ym)} {yearOf(months[pk].ym).slice(2)}
          </text>
        </g>
      )}

      {/* current balance */}
      <g className={`${k}-late`} style={{ pointerEvents:"none" }}>
        <circle cx={lx} cy={ly} r="9" style={{ fill:P.halo, fillOpacity:0.25 }} />
        <circle cx={lx} cy={ly} r="4.5" strokeWidth="2" style={{ fill:"#fff", stroke:P.ring }} />
        <text x={lx - 12} y={ly - 12} textAnchor="end"
              style={{ fontSize:16, fontWeight:800, fill:P.label }}>{short(bals[last])}</text>
      </g>

      {/* legend */}
      <g transform={`translate(${PADL}, ${TOP - 26})`} style={{ fontSize:10, fill:P.legend }}>
        <rect x="0" y="-7" width="14" height="3" rx="1.5" style={{ fill:P.line }} />
        <text x="20" y="-2">Balance</text>
        <rect x="80" y="-10" width="9" height="9" rx="2" fill={`url(#u${uid})`} />
        <text x="95" y="-2">Fundings + draws</text>
        <rect x="200" y="-10" width="9" height="9" rx="2" fill={`url(#d${uid})`} />
        <text x="215" y="-2">Principal + charge-offs</text>
        <circle cx="347" cy="-5.5" r="3" style={{ fill:P.dot }} />
        <text x="355" y="-2">Net change</text>
      </g>
    </svg>
  );
};

// ── small pieces ──────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <div style={{ fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase",
                letterSpacing:"0.06em", marginBottom:10 }}>{children}</div>
);

const Stat = ({ label, value, note }) => (
  <Card style={{ padding:"12px 14px" }}>
    <div style={{ fontSize:11, color:INK_MUTED, textTransform:"uppercase",
                  letterSpacing:"0.05em", marginBottom:4 }}>{label}</div>
    <div style={{ fontSize:19, fontWeight:600, color:INK_PRIMARY,
                  fontVariantNumeric:"tabular-nums" }}>{value}</div>
    {note && <div style={{ fontSize:11, color:INK_MUTED, marginTop:3 }}>{note}</div>}
  </Card>
);

// `walk` tints the five columns that move the balance; `bal` marks the two
// results. A reader should be able to see which columns add up to the answer
// without reading the help text first.
const Hd = ({ children, num: isNum, walk, bal, dim, help, sticky,
              onToggle, open, child }) => (
  <th title={help}
      onClick={onToggle || undefined}
      style={{ textAlign:isNum ? "right" : "left", padding:"8px 10px", whiteSpace:"nowrap",
               fontSize:10.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em",
               color: dim ? INK_MUTED : INK_PRIMARY,
               background: bal ? B.cyanLight : walk ? SURFACE_MUTED : SURFACE,
               borderBottom:"1.5px solid " + HAIRLINE_FIRM,
               // An opened child sits under its own total, so it is indented
               // by a hairline rather than by space the column cannot spare.
               borderLeft: child ? "2px solid " + B.cyanLight : undefined,
               position:sticky ? "sticky" : undefined, top:sticky ? 0 : undefined,
               cursor:onToggle ? "pointer" : help ? "help" : undefined,
               userSelect:onToggle ? "none" : undefined }}>
    {onToggle && (
      <span style={{ fontSize:8, color:INK_MUTED, marginRight:4 }} aria-hidden="true">
        {open ? "\u25bc" : "\u25b6"}
      </span>
    )}
    {children}{help && <span style={{ color:INK_MUTED, fontWeight:400 }}> {"\u24d8"}</span>}
  </th>
);

// `cap` clamps a cell to a width and truncates with an ellipsis rather than
// wrapping — a wrapped cell makes its whole row two lines tall, which costs
// more vertical space than the column saves horizontally. The full value goes
// in the title so nothing is lost.
//
// The clamp lives on an inner div, not the <td>. In an auto-layout table a
// cell's max-width is a suggestion the layout algorithm may ignore once the
// content is wide enough, so an account with four facilities would stretch the
// column anyway. A block child has an ordinary max-width that always holds.
const Cl = ({ children, num: isNum, cap, title }) => (
  <td title={cap ? title : undefined}
      style={{ padding:"5px 10px", textAlign:isNum ? "right" : "left",
               whiteSpace:"nowrap", color:INK_PRIMARY,
               ...(cap ? { width:cap, maxWidth:cap, cursor:"help" } : {}) }}>
    {cap
      ? <div style={{ maxWidth:cap, overflow:"hidden",
                      textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{children}</div>
      : children}
  </td>
);

const Row = ({ o, cols, label, bold, indent, total }) => (
  <tr style={{ borderTop:"1px solid " + HAIRLINE,
               background: total ? SURFACE_MUTED : indent ? SURFACE : undefined,
               fontWeight: bold || total ? 700 : 400 }}>
    <td style={{ padding:"5px 10px", paddingLeft:indent ? 30 : 10, whiteSpace:"nowrap",
                 color:indent ? INK_MUTED : INK_PRIMARY,
                 borderTop: total ? "2px solid " + HAIRLINE_FIRM : undefined }}>
      {label}
    </td>
    {cols.map(c => (
      <td key={c.key} style={{ padding:"5px 10px", textAlign:"right", whiteSpace:"nowrap",
                               color: c.dim ? INK_MUTED : INK_PRIMARY,
                               // matches the header rule, so an opened group
                               // reads as a block rather than three loose
                               // columns that happen to sit together
                               borderLeft: PART_DETAIL.includes(c.key)
                                 ? "2px solid " + B.cyanLight : undefined,
                               borderTop: total ? "2px solid " + HAIRLINE_FIRM : undefined }}>
        {cell(c, o[c.key])}
      </td>
    ))}
    {BAL_COLS.map(c => (
      <td key={c.key} style={{ padding:"5px 10px", textAlign:"right", whiteSpace:"nowrap",
                               fontWeight:600, background:B.cyanLight, color:INK_PRIMARY,
                               borderTop: total ? "2px solid " + HAIRLINE_FIRM : undefined }}>
        {fmt(o[c.key])}
      </td>
    ))}
  </tr>
);
